/**
 * RawDataButton Component
 *
 * Reusable button that shows raw JSON data for any document in a modal.
 * Perfect for debugging, checking relationships, and viewing hidden fields.
 *
 * Design Principle: Add this to ALL detail pages to allow inspection of raw data
 */

import React, { useState } from "react";
import { FaCode, FaTimes, FaCopy, FaCheck } from "react-icons/fa";
import PropTypes from "prop-types";
import { useAuth } from "../../auth/AuthContext";
import "./RawDataButton.scss";

export const RawDataButton = ({ data, label = "", buttonVariant = "secondary" }) => {
  const { isAdmin } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);

  // Only show for admin users
  if (!isAdmin) {
    return null;
  }

  const handleCopy = () => {
    const jsonString = JSON.stringify(data, null, 2);
    navigator.clipboard.writeText(jsonString);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatJson = (obj) => {
    return JSON.stringify(obj, null, 2);
  };

  return (
    <>
      <button
        className={`raw-data-button raw-data-button--${buttonVariant}`}
        onClick={() => setShowModal(true)}
        title="View raw JSON data"
      >
        <FaCode />
        {label && <span>{label}</span>}
      </button>

      {showModal && (
        <div className="raw-data-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="raw-data-modal" onClick={(e) => e.stopPropagation()}>
            <div className="raw-data-modal__header">
              <div className="raw-data-modal__title">
                <FaCode />
                <span>Raw JSON Data</span>
              </div>
              <div className="raw-data-modal__actions">
                <button
                  className="raw-data-modal__copy-btn"
                  onClick={handleCopy}
                  title={copied ? "Copied!" : "Copy to clipboard"}
                >
                  {copied ? <FaCheck /> : <FaCopy />}
                  {copied ? "Copied!" : "Copy"}
                </button>
                <button
                  className="raw-data-modal__close-btn"
                  onClick={() => setShowModal(false)}
                  title="Close"
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            <div className="raw-data-modal__body">
              <pre className="raw-data-modal__json">{formatJson(data)}</pre>
            </div>

            <div className="raw-data-modal__footer">
              <div className="raw-data-modal__info">
                <span className="raw-data-modal__count">
                  {Object.keys(data || {}).length} fields
                </span>
                <span className="raw-data-modal__hint">
                  💡 Use this to inspect relationships, check field names, and debug data
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

RawDataButton.propTypes = {
  data: PropTypes.object.isRequired,
  label: PropTypes.string,
  buttonVariant: PropTypes.oneOf(["primary", "secondary", "ghost"]),
};
